import React from "react";
import { motion } from "framer-motion";
import { ChevronRight, Sparkles, Lock, Rocket } from "lucide-react";

export default function ComingSoon({ module }) {
  const Icon = module.icon;
  return (
    <div data-testid={`coming-soon-${module.path.replace(/\//g, "-").replace(/^-/, "")}`}>
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-xs text-[#737373] mb-4">
        <span>Home</span>
        <ChevronRight className="w-3 h-3" />
        <span>{module.category}</span>
        <ChevronRight className="w-3 h-3" />
        <span className="text-[#A3A3A3]">{module.label}</span>
      </div>

      {/* Hero */}
      <motion.div
        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}
        className="relative overflow-hidden rounded-md border border-[#262626] bg-[#141414] p-8 mb-6"
      >
        <div className="absolute -top-20 -right-16 w-72 h-72 rounded-full bg-[radial-gradient(circle,rgba(212,175,55,0.1),transparent_70%)]" />
        <div className="relative flex items-start gap-5">
          <div className="w-14 h-14 rounded-md bg-[rgba(212,175,55,0.08)] border border-[rgba(212,175,55,0.2)] flex items-center justify-center flex-shrink-0">
            <Icon className="w-7 h-7 text-[#D4AF37]" strokeWidth={1.5} />
          </div>
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="font-heading text-3xl font-bold tracking-tight text-[#F5F5F5]">{module.label}</h1>
              <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.16em] font-semibold text-[#D4AF37] bg-[rgba(212,175,55,0.1)] border border-[rgba(212,175,55,0.25)] px-2.5 py-1 rounded-full">
                <Sparkles className="w-3 h-3" /> Coming Soon
              </span>
              {module.badge && (
                <span className={module.badge.tone === "gold"
                  ? "text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-[#D4AF37] text-[#0A0A0A]"
                  : "text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-[#262626] text-[#A3A3A3]"}>
                  {module.badge.label}
                </span>
              )}
            </div>
            <p className="text-sm text-[#A3A3A3] mt-2 max-w-2xl leading-relaxed">{module.description}</p>
          </div>
        </div>
      </motion.div>

      {/* Planned sections within this module */}
      <div className="flex items-center gap-2 mb-3">
        <span className="text-[11px] uppercase tracking-[0.16em] text-[#525252] font-semibold">Planned in {module.label}</span>
        <div className="h-px flex-1 bg-[#1c1c1c]" />
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {module.sections.map((s, i) => (
          <motion.div
            key={s.title}
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 + i * 0.05 }}
            className="rounded-md border border-[#262626] bg-[#141414] p-5 hover:border-[#333333] transition-colors"
            data-testid={`section-card-${i}`}
          >
            <div className="flex items-start justify-between">
              <span className="w-7 h-7 rounded-sm bg-[rgba(212,175,55,0.08)] text-[#D4AF37] text-xs font-bold flex items-center justify-center font-mono">{i + 1}</span>
              <Lock className="w-3.5 h-3.5 text-[#525252]" />
            </div>
            <h3 className="font-heading font-semibold text-[#F5F5F5] mt-3">{s.title}</h3>
            <p className="text-[13px] text-[#A3A3A3] mt-1 leading-relaxed">{s.desc}</p>
          </motion.div>
        ))}
      </div>

      <div className="mt-6 rounded-md border border-dashed border-[#333333] bg-[#0D0D0D] px-5 py-4 flex items-center gap-3">
        <Rocket className="w-4 h-4 text-[#D4AF37] flex-shrink-0" />
        <p className="text-xs text-[#737373]">This module is scaffolded and will be built out section by section. The structure above reflects what's planned for <span className="text-[#A3A3A3]">{module.label}</span>.</p>
      </div>
    </div>
  );
}
